import { useSearchParams } from "react-router-dom";
import { get } from "../../services/api";
import type { PeoplePage } from "../../services/social";
import { useAsync, useTitle } from "../../hooks";
import { useAuth } from "../../hooks/AuthContext";
import { ErrorBox, Loading, Pagination } from "../../components/Ui";
import { CommunityAside } from "../../components/CommunityAside";
import { PeopleList } from "../../components/PeopleList";
import { Icon } from "../../components/Icon";

export function People({ following = false }: { following?: boolean }) {
  useTitle(following ? "Following" : "People");
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const page = Math.max(
    1,
    Math.min(10000, Math.floor(Number(params.get("page")) || 1)),
  );
  const result = useAsync(
    () =>
      get<PeoplePage>(
        `${following ? `/users/${encodeURIComponent(user?.username ?? "")}/following` : "/users"}?perPage=20&page=${page}&q=${encodeURIComponent(q)}`,
      ),
    [q, page, following, user?.username],
  );
  return (
    <div className="community-layout">
      <section className="community-stream">
        <div className="stream-heading">
          <h1>{following ? "Following" : "People"}</h1>
        </div>
        <div className="people-introduction">
          <h2>
            {following
              ? "Your circle of knowledge."
              : "Find your kind of curious."}
          </h2>
          <p className="muted">
            {following
              ? "The people you follow and the knowledge they share."
              : "Researchers, builders, and collectors of useful ideas. Explore their wikis and follow along."}
          </p>
          <label className="people-search">
            <Icon name="search" />
            <input
              type="search"
              aria-label="Search people"
              placeholder="Search names and bios"
              value={q}
              maxLength={100}
              onChange={(event) =>
                setParams(event.target.value ? { q: event.target.value } : {}, {
                  replace: true,
                })
              }
            />
          </label>
        </div>
        {result.loading && !result.data ? (
          <Loading />
        ) : result.error ? (
          <ErrorBox error={result.error} retry={result.reload} />
        ) : (
          <>
            <PeopleList people={result.data?.items ?? []} />
            <Pagination
              page={page}
              perPage={20}
              total={result.data?.total ?? 0}
              onPage={(value) =>
                setParams({
                  ...Object.fromEntries(params),
                  page: String(value),
                })
              }
            />
          </>
        )}
      </section>
      <CommunityAside />
    </div>
  );
}
